import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingService } from '../../services/bookingService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
import { formatTimeToArabic } from '../../utils/formatDate';

const BookingExpired = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (bookingId) {
      loadBooking();
    }
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getBookingById(bookingId);
      setBooking(data);
    } catch (error) {
      console.error('Failed to load booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      await bookingService.retryBooking(bookingId);
      navigate(`/booking-waiting/${bookingId}`);
    } catch (error) {
      console.error('Failed to retry booking:', error);
      alert('فشل إعادة المحاولة. يرجى المحاولة مرة أخرى.');
    } finally {
      setRetrying(false);
    }
  };

  const handleNewBooking = () => {
    navigate('/booking-wizard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const canRetry = booking?.attemptCount < 3;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-4">
            <BackButton to="/customer-dashboard/bookings" label="العودة إلى الحجوزات" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12 text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="XCircle" size={48} className="text-error" />
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              {canRetry ? 'لم يقبل المصور الطلب' : 'انتهت جميع المحاولات'}
            </h1>

            {/* Message */}
            <p className="text-lg text-slate-600 mb-8">
              {canRetry 
                ? 'اعتذر المصور عن قبول طلبك. يمكنك البحث عن مصور آخر أو تعديل الموعد.'
                : 'اعتذر المصورون عن قبول طلبك 3 مرات. يمكنك تعديل الموعد أو المحاولة في وقت آخر.'}
            </p>

            {/* Booking Summary */}
            {booking && (
              <div className="bg-slate-50 rounded-xl p-6 mb-8 text-right">
                <h3 className="font-semibold text-slate-900 mb-4">تفاصيل الطلب</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">نوع الخدمة</span>
                    <span className="font-medium text-slate-900">
                      {getPhotographyTypeLabelAr(booking.photographyType || booking.serviceType)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">المدينة</span>
                    <span className="font-medium text-slate-900">{booking.city || 'الرياض'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">التاريخ والوقت</span>
                    <span className="font-medium text-slate-900">
                      {booking.bookingDate 
                        ? new Date(booking.bookingDate).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'غير محدد'}{' '}
                      - {formatTimeToArabic(booking.bookingTime)}
                    </span>
                  </div>
                  {booking.attemptCount > 0 && (
                    <div className="flex justify-between border-t border-slate-200 pt-3 mt-3">
                      <span className="text-slate-600">عدد المحاولات</span>
                      <span className="font-medium text-slate-900">
                        {booking.attemptCount} من 3
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-4">
              {canRetry && (
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleRetry}
                  loading={retrying}
                  iconName="RefreshCw"
                  iconPosition="right"
                >
                  ابحث عن مصور آخر
                </Button>
              )}
              
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={handleNewBooking}
                iconName="Plus"
                iconPosition="right"
              >
                حجز جديد
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigate('/customer-dashboard/bookings')}
                iconName="ArrowRight"
              >
                العودة إلى الحجوزات
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingExpired;
